import { BoltConnectionWriteResponse, ProjectWriteResponse } from '../_generated/api/structure'
import DirectoryEntry from '../models/DirectoryEntry'

export type StructureWriteResponse = Pick<
    ProjectWriteResponse | BoltConnectionWriteResponse,
    'addedOrModified' | 'removed'
>

const compareOrderKeys = (a: DirectoryEntry, b: DirectoryEntry) =>
    a.orderKey < b.orderKey ? -1 : a.orderKey > b.orderKey ? 1 : 0

const removeNode = (nodes: DirectoryEntry[], id: string): DirectoryEntry[] =>
    nodes
        .filter((node) => node.id !== id)
        .map((node) => node.withChildren(removeNode(node.children, id)))

const replaceNode = (
    nodes: DirectoryEntry[],
    replacement: DirectoryEntry
): [DirectoryEntry[], boolean] => {
    let replaced = false
    const updated = nodes.map((node) => {
        if (node.id === replacement.id) {
            replaced = true
            return replacement
        }
        const [children, childReplaced] = replaceNode(node.children, replacement)
        if (!childReplaced) return node
        replaced = true
        return node.withChildren(children)
    })
    return [updated, replaced]
}

const insertNode = (
    nodes: DirectoryEntry[],
    addition: DirectoryEntry
): [DirectoryEntry[], boolean] => {
    let inserted = false
    const updated = nodes.map((node) => {
        if (node.id === addition.parentId) {
            inserted = true
            return node.withChildren([...node.children, addition].sort(compareOrderKeys))
        }
        const [children, childInserted] = insertNode(node.children, addition)
        if (!childInserted) return node
        inserted = true
        return node.withChildren(children)
    })
    return [updated, inserted]
}

export const applyStructureWriteResponse = (
    currentRoots: DirectoryEntry[],
    response: StructureWriteResponse
) => {
    let roots = currentRoots

    response.removed?.forEach(({ id }) => {
        roots = removeNode(roots, id)
    })
    response.addedOrModified?.forEach((node) => {
        const addition = new DirectoryEntry(node)
        const [replacedRoots, replaced] = replaceNode(roots, addition)
        if (replaced) {
            roots = replacedRoots
            return
        }
        if (addition.parentId == null) {
            roots = [...roots, addition].sort(compareOrderKeys)
            return
        }
        const [insertedRoots, inserted] = insertNode(roots, addition)
        if (!inserted) throw new Error(`Structure parent ${addition.parentId} was not found`)
        roots = insertedRoots
    })

    return roots
}
